import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TbEdit } from "react-icons/tb";
import { Tooltip } from "antd";

import editIcon from "../../assets/icons/edit.svg";
import deleteIcon from "../../assets/icons/delete.svg";

import { useThunk } from "../../hooks/useThunk";
import { useInput } from "../../hooks/use-input";
import { useFileInput } from "../../hooks/use-file-input";
import showToast from "../../utils/toastNotifications";
import { validateRemixerName } from "../../utils/validationUtils";
import { processImageFile } from "../../utils/processImageFile";
import {
  capitalizeEachWord,
  trimTextWithEllipsis,
} from "../../utils/formatting";
import {
  createRemixer,
  getAllRemixers,
  updateRemixer,
  deleteRemixer,
} from "../../services/thunks/remixerThunks";
import {
  uploadToCloudflare,
  deleteFromCloudflare,
} from "../../services/thunks/cloudFlareThunks";

import Input from "../../components/Form/Input";
import CustomButton from "../../components/Buttons/CustomButton";
import Table from "../../components/Table/Table";
import ConfirmDeleteModal from "../../components/Modals/ConfirmDeleteModal";
import FileUpload from "../../components/Form/FileUpload";
import CountryDropdown from "../../components/Form/CountryDropdown";
import SearchBar from "../../components/common/SearchBar";
import CloudflareImage from "../../components/CloudflareImage";
import FilterDropdown from "../../components/FilterDropdown";

import { countryData } from "../../constants/countries";


const ManageRemixers = () => {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [doCreateRemixer, isCreatingRemixer] = useThunk(createRemixer);
  const [doUpdateRemixer, isUpdatingRemixer] = useThunk(updateRemixer);
  const [doDeleteRemixer, isDeletingRemixer] = useThunk(deleteRemixer);
  const [doUploadToCloudflare, isUploadingToCloudflare] =
    useThunk(uploadToCloudflare);
  const [doDeleteFromCloudflare, isDeletingFromCloudflare] =
    useThunk(deleteFromCloudflare);

  const {
    value: remixerName,
    handleInputChange: handleRemixerNameChange,
    handleInputBlur: handleRemixerNameBlur,
    hasError: remixerNameHasError,
    errorMessage: remixerNameErrorMessage,
    setErrorMessage,
    reset: resetRemixerName,
  } = useInput("", (val) => validateRemixerName(val.trim()));

  const {
    file: remixerProfilePicture,
    handleFileChange: handleRemixerImageChange,
    handleFileDrop: handleRemixerImageDrop,
    hasError: remixerImageHasError,
    error: remixerImageError,
    reset: resetProfilePicture,
  } = useFileInput();

  const {
    value: country,
    handleInputChange: handleCountryChange,
    handleInputBlur: handleCountryBlur,
    setValue: setCountry,
    reset: resetCountry,
  } = useInput("", (value) => value !== "");

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedRemixer, setSelectedRemixer] = useState(null);
  const [rowStates, setRowStates] = useState({});
  const [filterOption, setFilterOption] = useState("all");
  const [isUploading, setIsUploading] = useState(false);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const tableRef = useRef(null);
  const pageSetterRef = useRef(null);
  const tableUpdateRowRef = useRef(null);
  const tableRemoveRowRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
        setRowStates({});
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async () => {
    if (!isValidForm) {
      return;
    }

    const formattedName = capitalizeEachWord(remixerName.trim());

    try {
      let profilePictureUrl = remixerProfilePicture;

      if (remixerProfilePicture instanceof File) {
        const uploadResult = await doUploadToCloudflare({
          file: profilePictureUrl,
          type: "images",
        });

        const url =
          uploadResult?.response?.url ||
          uploadResult?.data?.url ||
          uploadResult?.payload?.url ||
          uploadResult?.url;

        if (!uploadResult.success || !url) {
          throw new Error(uploadResult.error || "Failed to upload image");
        }

        profilePictureUrl = url;
      }

      const result = await doCreateRemixer({
        token: auth.token,
        payload: {
          remixerName: formattedName,
          remixerProfilePicture: profilePictureUrl,
          country: country,
        },
      });
      if (result.success) {
        const newId = result.response?.data?.remixer?._id || result.id;
        const newIndex = result.response?.data?.index;
        showToast("success", `Remixer ‘${formattedName}’ added successfully.`);
        resetRemixerName();
        resetProfilePicture();
        resetCountry();
        setCountry("");
        setFileUploadKey((prev) => prev + 1);

        const pageSize = 10;
        const pageNumber = Math.floor(newIndex / pageSize) + 1;

        if (pageSetterRef.current) {
          pageSetterRef.current(pageNumber);
        }

        setHighlightedRowId(newId);

        setTimeout(() => {
          setHighlightedRowId(null);
        }, 5000);

        setRefreshTrigger((prev) => prev + 1);
      } else {
        setErrorMessage(result.error.message);
      }
    } catch (error) {
      showToast("error", error.message || "An error occurred");
    }
  };

  const handleDelete = async (record) => {
    const result = await doDeleteRemixer({ token: auth.token, id: record._id });
    if (result.success) {
      showToast("success", "Remixer deleted successfully.");

      if (tableRemoveRowRef.current) {
        tableRemoveRowRef.current(record._id);
      }

      const updatedNA = result.response?.data?.naRemixer;
      if (updatedNA && tableUpdateRowRef.current) {
        tableUpdateRowRef.current(updatedNA._id, updatedNA);
      }

      if (record.remixerProfilePicture) {
        const imageUrl = record.remixerProfilePicture;
        const imageKey = imageUrl ? imageUrl.split("/").pop() : null;
        const deleteResult = await doDeleteFromCloudflare({ key: imageKey });
        if (!deleteResult.success) {
          showToast(
            "error",
            deleteResult.error.message ||
            "Failed to delete image from Cloudflare"
          );
        }
      }
    } else {
      showToast("error", result.error.message);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const handleFilterChange = (value) => {
    setFilterOption(value);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleRowSave = React.useCallback(
    async (id) => {
      setEditMode(true);
      const rowData = rowStates[id];
      const remixerName = capitalizeEachWord(rowData.remixerName?.trim() || "");
      const country = rowData.country || "";

      let profilePictureUrl = rowData.image;

      if (profilePictureUrl instanceof File) {
        const uploadResult = await doUploadToCloudflare({
          file: profilePictureUrl,
          type: "images",
        });

        const url =
          uploadResult?.response?.url ||
          uploadResult?.data?.url ||
          uploadResult?.payload?.url ||
          uploadResult?.url;

        if (!uploadResult.success || !url) {
          showToast("error", uploadResult.error || "Failed to upload image");
          return;
        }

        profilePictureUrl = url;
      }

      const result = await doUpdateRemixer({
        token: auth.token,
        id,
        payload: {
          remixerName,
          country,
          remixerProfilePicture: profilePictureUrl,
        },
      });

      if (result.success) {
        showToast("success", "Remixer updated successfully.");
        setRowStates((prev) => ({
          ...prev,
          [id]: { editing: false },
        }));

        if (tableUpdateRowRef.current) {
          tableUpdateRowRef.current(id, {
            remixerName,
            country,
            remixerProfilePicture: profilePictureUrl,
          });
        }

        setHighlightedRowId(id);
        setTimeout(() => setHighlightedRowId(null), 5000);
        setEditMode(false);
      } else {
        showToast("error", result.error.message);
        setEditMode(false);
      }
    },
    [
      rowStates,
      doUploadToCloudflare,
      doUpdateRemixer,
      auth.token,
      setRowStates,
      setHighlightedRowId,
    ]
  );

  const handleSearch = () => {
    if (pageSetterRef.current) {
      pageSetterRef.current(1);
    }
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (pageSetterRef.current) {
        pageSetterRef.current(1);
      }
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const isValidForm = remixerName && !remixerNameHasError;

  const filterOptions = [
    { _id: "all", name: "All" },
    { _id: "withProfilePic", name: "With Profile Picture" },
    { _id: "withoutProfilePic", name: "Without Profile Picture" },
  ];

  const tableColumns = useMemo(
    () => [
      {
        title: "Profile",
        dataIndex: "remixerProfilePicture",
        key: "remixerProfilePicture",
        align: "center",
        minWidth: "270px",
        className: "profile-column",
        render: (remixerProfilePicture, record) => {
          const isEditing = rowStates[record._id]?.editing;
          const image = rowStates[record._id]?.image ?? remixerProfilePicture;
          const error = rowStates[record._id]?.error;

          if (isEditing) {
            return (
              <div className="flex flex-row items-start ml-10 ">
                <div className="relative group w-[40px] h-[40px]">
                  <CloudflareImage imageUrl={image} alt="Profile" />
                  <button
                    onClick={() =>
                      document.getElementById(`fileInput-${record._id}`).click()
                    }
                    className="absolute bottom-0 right-0 p-1 bg-slate-700 hover:bg-black text-white rounded-full transition"
                  >
                    <TbEdit className="text-white text-sm" />
                  </button>
                  <input
                    id={`fileInput-${record._id}`}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      if (!selectedFile) return;

                      if (selectedFile.size > 3 * 1024 * 1024) {
                        showToast(
                          "error",
                          "Image exceeds maximum allowed size of 3MB."
                        );
                        return;
                      }

                      setRowStates((prev) => ({
                        ...prev,
                        [record._id]: {
                          ...prev[record._id],
                          error: null,
                        },
                      }));

                      processImageFile(selectedFile, {
                        targetWidth: 500,
                        targetHeight: 500,
                        aspectRatio: 1,
                        quality: 0.75,
                      })
                        .then((formattedFile) => {
                          if (formattedFile.size > 150 * 1024) {
                            showToast(
                              "error",
                              "Compressed image exceeds 150KB."
                            );
                            return;
                          }

                          setRowStates((prev) => ({
                            ...prev,
                            [record._id]: {
                              ...prev[record._id],
                              image: formattedFile,
                              error: null,
                            },
                          }));
                        })
                        .catch((err) => {
                          showToast(
                            "error",
                            err.message || "Image processing failed"
                          );
                        });
                    }}
                  />
                </div>
              </div>
            );
          }

          return remixerProfilePicture ? (
            <div className="flex justify-start items-center ml-10">
              <CloudflareImage imageUrl={remixerProfilePicture} alt="Profile" />
            </div>
          ) : (
            <div className="ml-8 text-[#888] h-[40px] flex items-center text-sm italic">
              No Image
            </div>
          );
        },
      },
      {
        title: "Remixer Name",
        dataIndex: "remixerName",
        key: "remixerName",
        minWidth: "230px",
        render: (text, record) => {
          const isEditing = rowStates[record._id]?.editing;
          const value = rowStates[record._id]?.remixerName ?? text;

          return isEditing ? (
            <Input
              autoFocus
              type="text"
              value={value}
              onChange={(e) => {
                const newValue = e.target.value;
                setRowStates((prev) => ({
                  ...prev,
                  [record._id]: {
                    ...prev[record._id],
                    remixerName: newValue,
                  },
                }));
              }}
              inputStyle="bg-transparent border-0 border-b border-[#ccc] rounded-none shadow-none outline-none px-0 pb-0 pt-[1px] h-[28px] text-sm [400] leading-[18px] text-[#E2E2E2] font-inter"
            />
          ) : (
            <div className="min-h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2]">
              {text}
            </div>
          );
        },
      },
      {
        title: "Country",
        dataIndex: "country",
        key: "country",
        minWidth: "200px",
        render: (text, record, index) => {
          const isEditing = rowStates[record._id]?.editing;
          const value = rowStates[record._id]?.country ?? text;

          const countryObj = countryData.find((c) => c.name === value);

          const dropUp = 10 - index <= 3;

          return isEditing ? (
            <Tooltip placement="topLeft" title={countryObj?.name || value}>
              <div className="w-full ">
                <CountryDropdown
                  options={countryData}
                  placeholder="Select Country"
                  displayKey="name"
                  idKey="_id"
                  defaultOption={countryObj}
                  onSelect={(option) => {
                    setRowStates((prev) => ({
                      ...prev,
                      [record._id]: {
                        ...prev[record._id],
                        country: option?.name || "",
                      },
                    }));
                  }}
                  dropUp={dropUp}
                  wrapperClassName="text-[#E2E2E2] text-[15px] font-[300] font-['Public Sans'] border-0 border-[#ccc] rounded-none shadow-none outline-none px-0 pb-0 pt-[1px] h-[28px] w-full"
                  selectedValueClassName="min-h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2] mb-1"
                  buttonClassName="h-[28px] bg-transparent  border-transparent text-left w-full"
                  selectedButtonClassName="bg-transparent border-0 border-b border-[#ccc] rounded-none shadow-none outline-none px-0 pr-1 pb-0 pt-[3px] h-[28px] text-sm font-[400] leading-[18px] text-[#E2E2E2] flex items-center w-full min-h-[28px] "
                  dropdownClassName="bg-[#2D2D2D] border-[#808080] border"
                  truncateDisplay={true}
                  maxDisplayLength={20}
                  optionClassName="text-[#E2E2E2] py-2"
                  filterClassName="border-t border-[#505050]"
                  isSearchable={true}
                  allowClear={false}
                />
              </div>
            </Tooltip>
          ) : (
            <Tooltip placement="topLeft" title={value}>
              <span className="min-h-[32px] flex items-center text-sm font-[400] font-inter text-[#E2E2E2]">
                {trimTextWithEllipsis(value || "-", 20)}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: "Used In",
        dataIndex: "usedIn",
        key: "usedIn",
        align: "center",
        minWidth: "100px",
        render: (count) => (
          <div className="flex justify-center items-center bg-[#212121] rounded-[10px] w-[50px] h-[25px] leading-[24px] text-[#E2E2E2] text-sm text-center font-[400] font-inter">
            {count || 0}
          </div>
        ),
      },
      {
        title: "Action",
        key: "action",
        minWidth: "160px",
        render: (_, record) => {
          const isEditing = rowStates[record._id]?.editing;
          const value = rowStates[record._id]?.value ?? record.remixerName;
          const image =
            rowStates[record._id]?.image ?? record.remixerProfilePicture;

          return (
            <div className="flex gap-3 justify-start items-center">
              {isEditing ? (
                <>
                  <CustomButton
                    onClick={() =>
                      setRowStates((prev) => ({
                        ...prev,
                        [record._id]: {
                          editing: false,
                          remixerName: undefined,
                          image: undefined,
                          country: undefined,
                        },
                      }))
                    }
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#CCCCCC] rounded-[15px] text-xs font-normal text-[#CCCCCC] w-[90px] hover:bg-[#333] transition font-inter"
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    onClick={() => handleRowSave(record._id, value, image)}
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#00ffd5] rounded-[15px] text-xs font-normal text-[#00ffd5] w-[90px] hover:bg-[#00ffd53b] hover:text-white transition font-inter"
                    disabled={
                      !!rowStates[record._id]?.error ||
                      !value ||
                      isUpdatingRemixer ||
                      isUploadingToCloudflare
                    }
                    loading={isUpdatingRemixer || isUploadingToCloudflare}
                  >
                    Save
                  </CustomButton>
                </>
              ) : record.remixerName.toLowerCase() === "n/a" ? (
                <div className="text-xs text-[#888] italic">Locked</div>
              ) : (
                <>
                  <CustomButton
                    onClick={() => {
                      setRowStates({
                        [record._id]: {
                          editing: true,
                          remixerName: record.remixerName,
                          country: record.country,
                          image: record.remixerProfilePicture,
                        },
                      });
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#B4C007] text-[#B4C007] rounded-[15px] text-xs font-normal w-[90px] hover:bg-[#333300] transition font-inter"
                  >
                    <img src={editIcon} alt="edit" className="w-3 h-3" />
                    Edit
                  </CustomButton>
                  <CustomButton
                    onClick={() => {
                      setSelectedRemixer(record);
                      setShowConfirm(true);
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-1 border border-[#C00707] text-[#C00707] rounded-[15px] text-xs font-normal w-[90px] hover:bg-[#330000] transition font-inter"
                  >
                    <img src={deleteIcon} alt="delete" className="w-3 h-3" />
                    Delete
                  </CustomButton>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [rowStates, isUpdatingRemixer, isUploadingToCloudflare, handleRowSave]
  );

  return (
    <div className="px-6 py-10 text-white min-h-screen">
      {/* Form Section */}
      <div className="bg-[#111111D6] border border-[#505050] p-6 rounded-lg mb-8 w-full">
        <h2 className="text-xl font-bold font-['Public_Sans'] mb-4 text-[#E3E3E3]">
          Add New Remixers
        </h2>

        <div className="flex flex-col gap-4 xl:flex-row xl:gap-5 xl:h-[70px] items-start xl:items-end">
          {/* Remixer Name Input */}
          <div className="flex-1 w-full">
            <Input
              label="Remixer Name"
              isRequired
              placeholder="Enter Remixer Name"
              value={remixerName}
              onChange={handleRemixerNameChange}
              onBlur={handleRemixerNameBlur}
              errorMessage={remixerNameErrorMessage}
              inputStyle="bg-[#45444433] border-[1px] border-[#808080] h-[45px]"
              outerContainerStyle="mt-[-1px]"
              labelStyle="font-['Public_Sans'] font-normal text-[14px] leading-[22px] flex items-center text-[#E3E3E3] whitespace-nowrap"
              placeholderStyle="placeholder:text-[#808080] placeholder:text-[14px] placeholder:font-['Public_Sans'] placeholder:font-normal"
            />
          </div>

          <div className="flex-1 w-full min-w-[220px]">
            <FileUpload
              label="Profile Image"
              key={fileUploadKey}
              type="image"
              maxSize={3}
              isRequired={false}
              uploadText="Select your file or photo"
              onFileUpload={handleRemixerImageDrop}
              allowRemove={true}
              file={remixerProfilePicture}
              error={remixerImageError}
              showErrorBelow={false}
              containerStyle="w-full min-w-[220px] w-full h-full "
              labelStyle="text-sm mb-[8px] font-medium font-['Public_Sans'] text-[#E3E3E3] whitespace-nowrap"
            />
          </div>

          <div className="flex-1 w-full">
            <CountryDropdown
              label="Country"
              options={countryData}
              placeholder="Select Country"
              displayKey="name"
              idKey="name"
              wrapperClassName="text-[#E2E2E2] text-[15px] font-[300] font-['Public Sans'] w-full min-w-[281px]"
              dropdownClassName="bg-[#2D2D2D] border-[#808080] border z-20"
              buttonClassName="h-[45px] border-[#808080]"
              optionClassName="text-[#E2E2E2] py-2"
              filterClassName="border-t border-[#505050]"
              labelStyle="font-['Public_Sans'] font-medium text-[#E3E3E3] whitespace-nowrap"
              placeholderClassName="text-[#808080] text-[14px] font-['Public Sans'] font-normal"
              isSearchable={true}
              allowClear={false}
              defaultOption={countryData.find((c) => c.name === country)}
              onSelect={(option) => setCountry(option?.name || "")}
            />
          </div>

          {/* Submit Button */}
          <CustomButton
            useGradientBorder
            rounded="rounded-lg"
            textSize="text-[18px] leading-[24px]"
            padding="py-[10.5px] px-[13.5px]"
            font="font-public-sans"
            border="border-lg"
            type="button"
            width="w-[147px] h-[42px]"
            onClick={handleSubmit}
            disabled={
              !isValidForm || isUploadingToCloudflare || isCreatingRemixer
            }
            loading={
              (!editMode && isUploadingToCloudflare) || isCreatingRemixer
            }
          >
            Add Remixer
          </CustomButton>
        </div>
      </div>

      {/* Search Section */}
      <div className="flex items-center gap-4 mb-6 ">
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setRefreshTrigger={setRefreshTrigger}
          placeholder="Search Remixers..."
          containerClass="bg-[#45444433] border border-[#808080] rounded-[5px] px-3   w-full h-[45px]"
          inputClass="bg-transparent outline-none text-sm ml-3 w-full text-[#E2E2E2] font-[400] font-['Public Sans'] pr-6"
          buttonClass="absolute right-3 text-[#A0A0A0] hover:text-white text-lg font-bold"
          onKeyPress={handleKeyPress}
        />

        <FilterDropdown
          options={filterOptions}
          filterValue={filterOption}
          onSelect={(option) => handleFilterChange(option._id)}
        />

        <CustomButton
          className="rounded-[3.77px] h-[45px] w-[91px] min-w-[91px] text-[11px] uppercase font-['Public_Sans'] font-normal"
          textColor="text-[#51F9EF]"
          borderColor="border-[#51F9EF]"
          onClick={handleSearch}
        >
          Search
        </CustomButton>
      </div>

      {/* Table Section */}
      <div ref={tableRef}>
        <Table
          columns={tableColumns}
          fetchData={getAllRemixers}
          refreshTrigger={refreshTrigger}
          rowKey="_id"
          dataKey="items"
          headingBgColor="#0D141B"
          headingTextColor="#FFFFFF"
          customQueryParams={(params) => ({
            ...params,
            token: auth.token,
            remixerName: searchQuery.trim() || undefined,
            hasProfilePicture:
              filterOption === "withProfilePic"
                ? true
                : filterOption === "withoutProfilePic"
                  ? false
                  : undefined,
          })}
          highlightedRowId={highlightedRowId}
          setPageCallback={(fn) => (pageSetterRef.current = fn)}
          searchQuery={searchQuery}
          updateRowRef={tableUpdateRowRef}
          removeRowRef={tableRemoveRowRef}
        />
      </div>

      <ConfirmDeleteModal
        visible={showConfirm}
        onConfirm={async () => {
          await handleDelete(selectedRemixer);
          setShowConfirm(false);
          setSelectedRemixer(null);
        }}
        onCancel={() => {
          setShowConfirm(false);
          setSelectedRemixer(null);
        }}
        title="Confirm Deletion"
        content={`Are you sure you want to delete the remixer "${selectedRemixer?.remixerName}"?`}
        disabled={isDeletingRemixer || isDeletingFromCloudflare}
        loading={isDeletingRemixer || isDeletingFromCloudflare}
      />
    </div>
  );
};

export default ManageRemixers;
